import { FC, useCallback, useEffect, useRef, useState } from 'react';
import DateUtils from 'utils/dateUtils';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { CustomButton, CustomInput, CustomModal, If, InfiniteLoadingSpinner } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import { Radio } from '@mui/material';
import { AssigneesIcon, CircleCloseIcon, JobsTabIcon, ThinSearchIcon } from 'assets/icons';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { usePaginatedGetActivePayRunsQuery } from '../hooks/usePaginatedGetActivePayRunsQuery';
import { debounce } from 'utils/commonUtils';

interface MoveJobToPayRunModalProps {
  isOpen: boolean;
  jobNumber?: string;
  onClose: () => void;
  handleMove: (payRunId: string) => void;
  currentPayRunId: string;
  isMoveJobLoading: boolean;
}

const MoveJobToPayRunModal: FC<MoveJobToPayRunModalProps> = ({
  isOpen,
  onClose,
  handleMove,
  jobNumber,
  currentPayRunId,
  isMoveJobLoading
}) => {
  const [selectedPayRun, setSelectedPayRun] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    list: payRuns,
    isLoading: isLoadingPayRuns,
    loadMore,
    hasMore: hasMorePayRuns,
    setQueryParams: setPayRunsQueryParams,
    queryParams: payRunsQueryParams
  } = usePaginatedGetActivePayRunsQuery(currentPayRunId);

  const payRunListLoader = useRef(loadMore);

  const { setIntersectionElement } = useIntersectionObserver({
    intersectionCallBack: payRunListLoader,
    threshold: 0.5
  });

  const initiateSearch = (searchTerm?: string) => {
    setPayRunsQueryParams(prevQueryParams => ({ ...prevQueryParams, searchValue: searchTerm }));
  };

  const debouncedSearchHandler = useCallback(debounce(initiateSearch, 500), [payRunsQueryParams]);

  const onSearchTermChange = (searchString: string) => {
    setSearchQuery(searchString);
    debouncedSearchHandler(searchString);
  };

  useEffect(() => {
    payRunListLoader.current = loadMore;
  }, [loadMore]);

  return (
    <CustomModal open={isOpen} width="w-full md:w-[518px]">
      <div className="flex items-start justify-between px-[30px] pt-6 pb-4">
        <div className="flex flex-col gap-2 text-headingGray">
          <span className="text-xl font-bold">Move {jobNumber ? 'job' : 'jobs'} to an open pay run</span>
          {jobNumber && <div className="text-sbase font-semibold">{jobNumber}</div>}
        </div>
        <CircleCloseIcon className="cursor-pointer" onClick={onClose} />
      </div>

      <div className="px-[30px] pb-6">
        <div className="rounded-md border border-borderGray p-4 pb-0">
          <CustomInput
            id="move-job-to-pay-run-search"
            value={searchQuery}
            onChange={onSearchTermChange}
            placeholder="Search pay runs"
            className="h-10 pr-3 pl-9"
            prefixWrapperStyle="left-3"
            prefix={<ThinSearchIcon />}
          />

          <div className="mt-4 max-h-[419px] min-h-[200px] overflow-y-auto">
            <If condition={payRuns?.length > 0}>
              {payRuns?.map(payRun => (
                <div
                  key={payRun.payRunId}
                  className="flex items-center justify-between border-t border-secondaryBtn py-4">
                  <Radio
                    checked={selectedPayRun === payRun.payRunId}
                    onChange={() => setSelectedPayRun(payRun.payRunId)}
                    className="p-0"
                  />
                  <div className="flex-1 pl-4">
                    <div className="mb-1.5 flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="text-sbase text-accentText">{payRun.payRunNumber}</div>
                        <span className="h-1 w-1 rounded-full bg-borderGray" />
                        <div className="flex gap-1 text-px13 text-primaryText">
                          <span className="flex items-center gap-1 rounded-[61px] bg-secondaryBtn px-1.5 py-0.5 ">
                            <AssigneesIcon />
                            {payRun.totalMembers}
                          </span>
                          <span className="flex items-center gap-1 rounded-[61px] bg-secondaryBtn px-1.5 py-0.5 ">
                            <JobsTabIcon className="w-3" />
                            {payRun.totalJobs}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center text-sbase font-semibold text-pending-orange">
                        <span className="mr-1 inline-block h-[5px] w-[5px] rounded-full bg-pending-orange" />
                        Open
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sbase font-bold text-primaryText">
                      <div>
                        {DateUtils.getDateInFormat({ date: new Date(payRun.payRunFrom), dateFormat: 'MMM dd, yyyy' })}-
                        <span>
                          {' '}
                          {DateUtils.getDateInFormat({ date: new Date(payRun.payRunTo), dateFormat: 'MMM dd, yyyy' })}
                        </span>
                      </div>
                      <div>{getAmountWithCurrency(payRun.totalAmount)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </If>
            <If condition={payRuns?.length === 0 && !isLoadingPayRuns}>
              <div className="py-3 text-center text-sbase font-semibold text-primaryText">No open pay runs found</div>
            </If>
            <If condition={!isLoadingPayRuns && hasMorePayRuns}>
              <div className="h-2" ref={setIntersectionElement}></div>
            </If>
            <If condition={isLoadingPayRuns}>
              <div className="py-3">
                <InfiniteLoadingSpinner />
              </div>
            </If>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-4 px-1">
          <CustomButton
            id="cancel-move-job-to-pay-run"
            onClick={onClose}
            type={ButtonType.SECONDARY}
            className="w-[100px]">
            Cancel
          </CustomButton>
          <CustomButton
            id="move-job-to-pay-run"
            onClick={() => handleMove(selectedPayRun)}
            disabled={!selectedPayRun || isLoadingPayRuns}
            isLoading={isMoveJobLoading}
            type={ButtonType.PRIMARY}
            className="w-[100px]">
            Move
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default MoveJobToPayRunModal;
